import { action, observable, toJS } from "mobx";
import { SportDataGateway, SportDataGatewayQueryInfo, BFFGateway, Observer, EventPushResponse, EventsResponse, EventPayload, EventsByEventIdRequest, EventsBySportIdRequest, DeviceChangeDispatcherType, deepMerge } from "sb-resp-lib";
import { ObservableData, ChangesCache, DataProvider, FilterPredicate, SubFilterPredicate, FilterFunctions, EventsRequest, EventsRequestType, Loader, FavoriteItem, TemporaryData } from "../typings";
import Debounce from "debounce-decorator";
import { bffRequestBuilder, queryBuilder, QueryParams, DateRangeMapper, BFFRequestParams, LoadedData, isDifferentResponse } from "../helpers";
import { DateRangeParam } from "../typings";
import { Event, Market, Entity, EventListPresenterRequest } from "../contracts";
import { Pagination, Sport } from "sbtech-online-sportcontent-sportbook";
import { uniqueElementsById } from "../helpers/UniqueElementsById/uniqueElementsById";
import { EntityType } from "sbtech-sports-data-api-contracts";

export class Retriever {
    private readonly FullLoadEventsCount: number = 100;
    private readonly ReloadTimeout: number = 60000;

    sub: Observer | undefined;
    sdapi: SportDataGateway;
    bff: BFFGateway;
    dataProvider: DataProvider;
    activeRequest: EventsRequest;
    filterPredicate: FilterPredicate;
    subFilterPredicate: SubFilterPredicate;
    deviceChangeDispatcher?: DeviceChangeDispatcherType;
    loadedEvents: number;
    reloadTimeout: number;

    query!: SportDataGatewayQueryInfo<EventPushResponse>;
    @observable data: ObservableData;
    @observable loader: Loader;
    loadedData: LoadedData;

    private temporaryData: TemporaryData;
    private changesCache: ChangesCache;
    private addsCache: ChangesCache;
    private applyAddref: Function;

    constructor(bff: BFFGateway, sdapi: SportDataGateway, dataProvider: DataProvider, deviceChangeDispatcherMock?: DeviceChangeDispatcherType) {
        this.bff = bff;
        this.sdapi = sdapi;
        this.dataProvider = dataProvider;
        this.activeRequest = { ...this.dataProvider.eventsRequest };
        this.deviceChangeDispatcher = deviceChangeDispatcherMock;
        this.data = { events: observable([]), markets: observable([]), leagues: observable([]) };
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.loadedEvents = 0;
        this.reloadTimeout = -1;
        this.loader = { isLoading: true, isFirstLoad: true, isFullLoadFinished: false };

        this.regularUpdate = this.regularUpdate.bind(this);
        this.regularUpdateTimeOut = this.regularUpdateTimeOut.bind(this);
        this.processError = this.processError.bind(this);
        this.applyAddref = this.applyAdd.bind(this);
        this.sub = undefined;
        this.filterPredicate = this.dataProvider.filterPredicate;
        this.subFilterPredicate = SubFilterPredicate.None;
        this.changesCache = { events: [], markets: [] };
        this.addsCache = { events: [], markets: [] };
        this.loadedData = new LoadedData();
    }

    @action activate() {
        this.setLoader(true);
        window.clearTimeout(this.reloadTimeout);
        this.clearData();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.activate();
    }

    @action deactivate() {
        this.setLoader(false);
        window.clearTimeout(this.reloadTimeout);
        this.clearData();
        this.sub && this.sub.unsubscribe();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.deactivate();
        this.filterPredicate = this.dataProvider.filterPredicate;
    }

    setParams(sportIds: string[]) {
        bffRequestBuilder.buildIds(this.activeRequest.request, sportIds);
    }

    /**
     * Update query filter to be for specific sport ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    public querySportUpdate(sportIds: string[]) {
        this.loader.isFullLoadFinished = false;
        this.loadedData.updateSports(sportIds) && (this.loader.isFirstLoad = true);

        this.setActiveRequest();
        const bffRequestParams = this.getBFFRequestParams();
        bffRequestBuilder.buildSportIdsUpdate(bffRequestParams, sportIds);
        this.filterPredicate = FilterPredicate.Sport;
        this.loadData();
    }

    /**
     * Update query filter to be for specific date range
     * @param {DateRangeProp} dateRange Newly selected date range
     */
    public queryDatesUpdate(dateRange: DateRangeParam, sportIds: string[]) {
        this.loader.isFullLoadFinished = false;
        this.loadedData.updateDates(dateRange.from || new Date()) && !this.loadedData.updateSports(sportIds)
            && (this.loader.isFirstLoad = true);

        this.setActiveRequest();
        const bffRequestParams = this.getBFFRequestParams();
        bffRequestBuilder.buildDateRangeUpdate(bffRequestParams, dateRange);
        this.filterPredicate = FilterPredicate.Date;
        this.loadData();
    }

    /**
     * Update query filter to be for specific league ids from sport ids
     * @param {string[]} leagueIds Newly selected league ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    public queryLeaguesUpdate(leagueIds: string[], sportIds: string[]) {
        this.loader.isFullLoadFinished = false;
        this.loader.isFirstLoad = true;
        this.setActiveRequest();
        const bffRequestParams = this.getBFFRequestParams();
        bffRequestBuilder.buildLeagueIdsUpdate(bffRequestParams, leagueIds, sportIds);
        this.filterPredicate = FilterPredicate.Sport;
        this.loadData();
    }

    /**
     * Update query filter to be for current tag
     * @param {string} currentTag Tag to be changed
     */
    public queryTagsUpdate(currentTag: EventsBySportIdRequest.EventTagsEnum) {
        this.loader.isFullLoadFinished = false;
        this.loadedData.updateTags(currentTag) && (this.loader.isFirstLoad = true);

        this.setActiveRequest();
        const bffRequestParams = this.getBFFRequestParams();
        bffRequestBuilder.buildEventTagsUpdate(bffRequestParams, [currentTag]);
        this.filterPredicate = FilterPredicate[currentTag.toString()];
        this.loadData();
    }

    /**
     * Update query filter to be for favourites
     * @param {string[]} eventIds Ids of favourite events
     */
    public queryFavoritesUpdate(favoriteEvents: FavoriteItem[]) {
        this.loader.isFullLoadFinished = false;
        this.loader.isFirstLoad = true;
        this.setActiveRequest(false);
        const bffRequestParams = this.getBFFRequestParams();
        bffRequestBuilder.buildEventIdsUpdate(bffRequestParams, favoriteEvents);
        this.filterPredicate = FilterPredicate.Favorites;
        this.loadData();
    }

    /**
     * Execute according request for fetching data on every x milliseconds - ReloadTimeout
     */
    public loadData() {
        if (this.activeRequest.request.ids.length) {
            window.clearTimeout(this.reloadTimeout);
            this.sub && this.sub.unsubscribe();
            this[`loadData${this.activeRequest.type}`]();
            this.reloadTimeout = window.setTimeout(() => this.loadData(), this.ReloadTimeout);
        }
    }

    /**
     * Process received data and query recursively if needed
     * @param {EventsResponse} response
     * @param {Pagination} pagination
     */
    private loadDataSuccessCb(response: EventsResponse, pagination: Pagination) {
        if (response.events && response.events.length) {
            this.loader.isFirstLoad ? this.loadNewData(response) : this.updateTemporaryData(response);

            if (response.events.length === pagination.top && this.loadedEvents < this.dataProvider.maxEventsToLoad) {
                return this.recalculatePagination(pagination);
            }
        }
        return false;
    }

    /**
     * Calculate request pagination despite the received amount of data
     * @param {Pagination} pagination Current pagination of the request. Empty by default.
     */
    private recalculatePagination(pagination: Pagination, shouldRecalcSkip: boolean = true) {
        const fullLoad = Math.min(this.dataProvider.maxEventsToLoad, this.FullLoadEventsCount);
        const loadedEvents = (pagination.top || 0) + (pagination.skip || 0);
        return {
            top: Math.min(this.dataProvider.maxEventsToLoad - this.loadedEvents, fullLoad),
            skip: shouldRecalcSkip ? loadedEvents : 0
        };
    }

    /**
     * Inital received data processing
     * @param {EventsResponse} response
     */
    private loadNewData(response: any) {
        this.add({ events: response.events, markets: response.markets });
        this.replaceLeagues({ leagues: response.leagues });
    }

    /**
    * renew Data on JWTToken change
    */
    public renewJWTToken() {
        this.loadData();
    }

    /**
    * sets the customers regionId
    * @param {regionId}  customers regionId
    */
    public setRegionId(regionId: string) {
        bffRequestBuilder.buildRegionIds(this.activeRequest.request, this.dataProvider.ipCountry.concat(regionId));
    }

    /**
    * removes the customers regionId
    *
    */
    public removeRegionId() {
        bffRequestBuilder.buildRegionIds(this.activeRequest.request, this.dataProvider.ipCountry);
    }

    /**
     * Helper function for updating current data and constructing new query for SportData API (request by top leagues)
     * @param {EventsResponse} response
     */
    private buildUpdateQueryByTopLeague(response: EventsResponse) {
        const queryParams = this.getQueryParams();
        this.query = queryBuilder.buildQueryTopLeagues(queryParams);
        this.updateData(queryParams.request, response);
    }

    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    private buildUpdateQueryByIds(response: EventsResponse) {
        const queryParams = this.getQueryParams();
        this.query = queryBuilder.buildQueryByEventIds(queryParams);
        this.updateData(queryParams.request, response);
    }

    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    private buildUpdateQueryByLeagueIds(response: EventsResponse) {
        const queryParams = this.getQueryParams();
        this.query = queryBuilder.buildQueryByLeagueIds(queryParams);
        this.updateData(queryParams.request, response);
    }

    /**
     * Subscribe for updates from SportData API
     */
    private querySub() {
        this.sub = this.sdapi.subscribeForEvents(this.query);
    }

    /**
     * Handler for update events - execute add,change or remove methods
     * @param {EventPushResponse} x
     */
    private regularUpdate(x: EventPushResponse) {
        Object.keys(x.data).forEach(prop => {
            let property = prop === "add" ? "addChanges" : prop;
            this[property](x.data[prop]);
        });
    }

    /**
     * Reset observable data
     */
    private clearData() {
        this.data = { events: observable([]), markets: observable([]), leagues: observable([]) };
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.loadedData.clearData();
    }

    // VERY IMPORTANT:
    // SDAPI is sending one event per change
    // 20 events - 20 time triggering the update logic and render
    // batching them and executing every half a second reduces
    // CPU usage to almost none
    /**
     * Handler for "Change" event from SportData API
     * @param {EventPayload} x
     */
    change(x: EventPayload<Event, Market>) {
        Object.keys(this.changesCache).map(prop => this.changesCache[prop].splice(this.changesCache[prop].length, 0, ...x[prop]));
        this.applyChanges();
    }

    @Debounce(200)
    private applyChanges() {
        this.batchedChange(this.changesCache);
        this.changesCache = { events: [], markets: [] };
    }

    @action.bound private replaceLeagues(x) {
        this.data.leagues.replace(uniqueElementsById(x.leagues.concat(this.data.leagues.slice())));
    }

    /**
     * Add and update currently stored data
     * @param {EventsResponse} x
     * @param {Function} filterFn Filter predicate
     */
    @action.bound private updateCurrentData(x: any, filterFn: Function) {
        let collections = FilterFunctions[this.filterPredicate].collections;
        Object.keys(this.data).forEach(collection => {
            const currentData = this.data[collection].slice();
            const dataToRemain = collections.indexOf(collection) > -1
                ? currentData.filter(e => !filterFn(e))
                : currentData;
            this.data[collection].replace(uniqueElementsById(dataToRemain.concat(x[collection])));
        });
    }
    /**
     * Handler for 'Add' update event from SportData API
     * @param {EventPayload} x
     */
    add(x: EventPayload<Event, Market>) {
        Object.keys(x).forEach(prop => this.data[prop].push.apply(this.data[prop], x[prop]));

    }

    addChanges(x) {
        Object.keys(this.addsCache).forEach(prop => this.addsCache[prop].concat(x[prop]));
        if (this.addsCache.events.length > 0 || this.addsCache.markets.length > 0) {
            this.applyAddref(this.addsCache);
        }
    }
    @Debounce(500)
    applyAdd() {
        this.batchedAdd(this.addsCache);
        this.addsCache = { events: [], markets: [] };
    }

    @action.bound batchedAdd(x) {
        let tempObject = { ...x };
        Object.keys(this.data).forEach(prop => {
            if (x[prop]) {
                tempObject[prop] = this.data[prop].concat(x[prop].slice());
                this.data[prop].replace(tempObject[prop]);
            }
            else {
                tempObject[prop] = this.data[prop].slice();
            }
        });
    }
    /**
     * Handler for 'Remove' update event from SportData API
     * @param {EventPayload} x
     */
    @action.bound remove(x: EventPayload<Event, Market>) {
        Object.keys(x).forEach(prop => {
            x[prop].forEach(y => {
                let i = this.data[prop].findIndex(z => z.id === y);
                if (i > -1) {
                    this.data[prop].splice(i, 1);
                }
            });
        });
    }

    @action.bound private batchedChange(x) {
        Object.keys(x).forEach(prop => {
            x[prop].forEach(y => {
                let i = this.data[prop].findIndex(z => z.id === y.id);
                if (i > -1) {
                    let item = deepMerge(toJS(this.data[prop][i]),y );
                    if (prop === "markets" && y.selections && y.selections.length) {
                        item.selections = y.selections;
                    }
                    this.data[prop][i] = item;
                }
            });
        });
    }

    /**
     * Get current filter function
     * @param {EventListPresenterRequest} request
     */
    getFilterFunction(request: EventListPresenterRequest) {
        const ids = this.activeRequest.type === EventsRequestType.ByLeagueIds
            ? request.marketTypeRequests.map(x => x.sportIds).reduce((x, y) => x.concat(y))
            : request.ids;
        let from, to;
        if (request.dateRange) {
            this.dataProvider.useNewTimeOnEveryRequest && (request.dateRange.from = new Date());
            from = request.dateRange.from;
            to = DateRangeMapper[request.dateRange.timeRange](from);
        }
        return this.getCurrentFilter.bind(this, ids, from, to);
    }

    /**
     * Bind the selected filter function parameters
     * @param {string[]} sportIds
     * @param {Date} from
     * @param {Date} to
     * @param {Entity} x
     */
    getCurrentFilter(sportIds: string[], from?: Date, to?: Date, x?: Entity) {
        return this[FilterFunctions[this.filterPredicate].fn](x, sportIds, from, to);
    }

    /**
     * Check if entity is from selected sportIds
     * @param {Entity} x
     * @param {string[]} sportIds
     */
    filterBySport(x: Entity, sportIds: string[]) {
        return sportIds.indexOf(x.sportId) > -1 && this.filterBySubPredicate(x as Event)
    };

    /**
     * Check if event has liveStream
     * @param {Event} x
     */
    filterByLiveStream(x: Event) {
        return this.filterByTag(x, EventsBySportIdRequest.EventTagsEnum.LiveStreaming.toString()) && this.filterBySubPredicate(x);
    }

    /**
     * Check if event has specific tag
     * @param {Event} x
     * @param {String} tag
     */
    filterByTag(x: Event, tag: string) {
        return x.tags.indexOf(tag) > -1;
    }

    /**
     * Check if Event or Market is in this date/time range
     * @param {Event|Market} x
     * @param {string[]} sportIds
     * @param {Date} from
     * @param {Date} to
     */
    filterBySportAndDate(x, sportIds: string[], from: Date, to: Date) {
        var eventDateTime = new Date(x.startEventDate || x.startDate);
        return this.filterBySport(x, sportIds) && (eventDateTime >= from && eventDateTime < to);
    }

    /**
     * Check if event is in favourites
     * @param {Event} x
     * @param {string[]} eventIds
     */
    filterByFavorites(x: Event, eventIds: string[]) {
        return eventIds.indexOf(x.id) > -1 && this.filterBySubPredicate(x);
    }

    /**
     * Request events by top leagues and sport ids
     */
    loadDataByTopLeagues() {
        this.loader.isFirstLoad && (this.loader.isLoading = true);
        this.loader.isFullLoadFinished = false;
        this.bff.getEventsFromTopLeaguesBySportId(this.activeRequest.request)
            .then(response => {
                if (isDifferentResponse(this.activeRequest.request, response)) {
                    return;
                }

                this.buildUpdateQueryByTopLeague(response);
                if (response.events && response.events.length < this.dataProvider.maxEventsToLoad &&
                    (response.events.length === 0 || !this.dataProvider.isTopLeaguesOnly)) {
                    this.loadedEvents = response.events.length;
                    const nextPagination = this.recalculatePagination({ top: response.events.length, skip: 0 }, false);
                    const request = bffRequestBuilder.buildSportIdRequest(this.activeRequest.request, nextPagination);
                    this.loadDataBySportId(request);
                } else {
                    this.loader.isLoading = false;
                    this.updateWithTemporaryData(this.activeRequest.request);
                }
            }).catch(this.processError);
    }

    /**
     * Request events by sport ids
     * @param {EventsBySportIdRequest} request Request for events from regular league ids
     */
    private loadDataBySportId(request: EventsBySportIdRequest) {
        this.bff.getEventsBySportId(request)
            .then(response => {
                if (isDifferentResponse(this.activeRequest.request, response)) {
                    return;
                }

                this.loadedEvents += (response.events && response.events.length) || 0;
                const nextPagination = this.loadDataSuccessCb(response, request.pagination || {});
                this.loader.isLoading = false;
                if (nextPagination) {
                    bffRequestBuilder.buildPagination(request, nextPagination);
                    this.loadDataBySportId(request);
                }
                else {
                    this.updateWithTemporaryData(request);
                }
            }).catch(this.processError);
    }

    private updateWithTemporaryData(request: EventsBySportIdRequest) {
        if (!this.loader.isFirstLoad) {
            let filterFn = this.getFilterFunction(request);
            this.temporaryData.leagues = uniqueElementsById(this.temporaryData.leagues);
            this.updateCurrentData(this.temporaryData, filterFn);
            this.temporaryData = { events: [], markets: [], leagues: [] };
        }
        this.loader.isFirstLoad = false;
        this.loader.isFullLoadFinished = true;
    }

    /**
     * Request events by event ids
     */
    loadDataByEventIds() {
        this.loader.isFirstLoad && (this.loader.isLoading = true);
        this.loader.isFullLoadFinished = false;
        this.bff.getEventsByEventId(this.activeRequest.request)
            .then(response => {
                this.buildUpdateQueryByIds(response);
                this.updateWithTemporaryData(this.activeRequest.request);
                this.setLoader(false);
            }).catch(this.processError);
    }

    /**
     * Request events by league ids
     */
    loadDataByLeagueIds() {
        this.loader.isFirstLoad && (this.loader.isLoading = true);
        this.loader.isFullLoadFinished = false;
        this.bff.getEventsByLeagueId(this.activeRequest.request)
            .then(response => {
                this.buildUpdateQueryByLeagueIds(response);
                this.updateWithTemporaryData(this.activeRequest.request);
                this.setLoader(false);
            }).catch(this.processError);
    }

    /**
     * Sets loader props
     * @param {boolean} isRequestInProcess Is request in process
     */
    @action.bound private setLoader(isRequestInProcess: boolean) {
        this.loader.isLoading = isRequestInProcess;
        this.loader.isFullLoadFinished = !isRequestInProcess;
    }

    /**
     * Process error from bff request
     */
    private processError(error: Error) {
        this.setLoader(false);
        // TODO: Process error
        console["error"](error);
    }

    /**
     * Update current data and subscribe for its updates
     * @param {EventListPresenterRequest} request Current request
     * @param {EventsResponse} response Received response
     */
    private updateData(request: EventListPresenterRequest, response: EventsResponse) {
        // Get the current filtering function and pass in
        let filterFn = this.getFilterFunction(request);
        this.loader.isFirstLoad ? this.updateCurrentData(response, filterFn) : this.updateTemporaryData(response);
        // Need to understand how we are going to pull more than 100 items
        // Probably going to be solved on the BFF

        // sub to query only after it arrives
        // missing some of the changes that happened during the pull
        // SDAPI team is working on improving performance of the pull
        this.querySub();
    }

    private updateTemporaryData(response: EventsResponse) {
        Object.keys(this.temporaryData).forEach(collection => {
            this.temporaryData[collection] = this.temporaryData[collection].concat(response[collection]);
        });
    }

    private filterBySubPredicate(x: Event): boolean {
        if (this.subFilterPredicate === SubFilterPredicate.None ||
            (x.entityType !== EntityType.Event && x.entityType.toString() !== Sport.EntityTypeEnum.Event.toString())) {
            return true;
        }

        return this.filterByTag(x, SubFilterPredicate[this.subFilterPredicate]);
    }

    /**
     * Set active BFF request to request by event ids
     * @param {boolean} isDefault Is default events request from data provider or not
     */
    private setActiveRequest(isDefault: boolean = true) {
        this.activeRequest = isDefault ? { ...this.dataProvider.eventsRequest } : {
            request: <EventsByEventIdRequest>{},
            type: EventsRequestType.ByEventIds
        };
    }

    /**
     * Get query params for query to SportData API
     */
    private getQueryParams(): QueryParams {
        return {
            request: this.activeRequest.request,
            messageHandler: this.regularUpdateTimeOut,
        };
    }
    private regularUpdateTimeOut(x: EventPushResponse) {
        setTimeout(this.regularUpdate.bind(this), 0, x);
    }
    /**
     * Get BFF request params for query to BFF
     */
    private getBFFRequestParams(): BFFRequestParams {
        return {
            request: this.activeRequest.request,
            cardConfiguration: this.dataProvider.isMiniEventCard ? this.dataProvider.cardConfigurationEventView
                : this.dataProvider.cardConfiguration,
            allSportIds: this.dataProvider.allSportIds
        };
    }
}
